import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, withLatestFrom, mergeMap, catchError, switchMap, concatMap, delay } from 'rxjs/operators';
import * as TimetrackActions from './timetrack.actions';
import { AuthSelectors, TimetrackSelectors, TimetrackService, ClientSelectors, SessionSelectors } from '@store';
import { EMPTY, of } from 'rxjs';
import { Timereg, KrKladde } from '@store/models';

@Injectable()
export class TimetrackEffects {

    selectStep$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.selectStep),
        map(() => TimetrackActions.loadStepData())
    ));

    loadStepData$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadStepData),
        withLatestFrom(
            this.timetrackSelector.step$,
            this.timetrackSelector.year$,
            this.timetrackSelector.date$
        ),
        switchMap(([_, step, year, date]) => {
            switch (step) {
                case 1:
                    return [
                        TimetrackActions.loadUgerapport({ year }),
                        TimetrackActions.loadUgerapportFaste({ year }),
                        TimetrackActions.loadNormtider({ year }),
                        TimetrackActions.loadUgerepDone({ year }),
                    ];
                case 2:
                    const dateYear = date.getFullYear();
                    return [
                        TimetrackActions.loadUgerapport({ year: dateYear }),
                        TimetrackActions.loadUgerapportFaste({ year: dateYear }),
                        TimetrackActions.loadUgerepDone({ year: dateYear }),
                    ];
                case 3:
                    return [
                        TimetrackActions.loadKroneKladde(),
                    ];
                case 4:
                    return [
                        TimetrackActions.loadKorebog()
                    ];
                case 5:
                    return [
                        TimetrackActions.loadOfficeUgerepDone()
                    ];
                default: return EMPTY;
            }
        })
    ));

    selectUser$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.selectUser),
        withLatestFrom(
            this.authSelector.userCard$,
            this.sessionSelector.usersSA$()
        ),
        map(([action, activeUser, users]) => {
            const user = action.user || users.find(u => u.cardId === activeUser.cardId);
            return TimetrackActions.selectUserSuccess({ user });
        })
    ));

    selectUserSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.selectUserSuccess),
        map(() => TimetrackActions.loadStepData())
    ));

    selectYear$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.selectYear),
        map(() => TimetrackActions.loadStepData())
    ));

    selectDate$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.selectDate),
        map(() => TimetrackActions.loadStepData())
    ));

    loadUgerapport$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadUgerapport),
        withLatestFrom(
            this.timetrackSelector.user$
        ),
        switchMap(([props, user]) => {
            if (!user) { return EMPTY; }
            return this.timetrackService.getUgerapport(user.cardId, props.year).pipe(
                map(sager => TimetrackActions.loadUgerapportSuccess({ sager })),
                catchError(() => EMPTY)
            );
        })
    ));

    loadUgerapportFaste$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadUgerapportFaste),
        withLatestFrom(
            this.timetrackSelector.user$
        ),
        switchMap(([props, user]) => {
            if (!user) { return EMPTY; }
            return this.timetrackService.getUgerapportFaste(user.cardId, props.year).pipe(
                map(sager => TimetrackActions.loadUgerapportFasteSuccess({ sager })),
                catchError(() => EMPTY)
            );
        })
    ));

    createTimereg$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.createTimereg),
        mergeMap(action => {
            let newTimereg = new Timereg();
            newTimereg = {...newTimereg, ...action.partialTimereg};
            return this.timetrackService.createTimereg(newTimereg).pipe(
                map(timereg => TimetrackActions.createTimeregSuccess({ timereg })),
                catchError(() => of(TimetrackActions.createTimeregFailed()))
            );
        })
    ));

    deleteTimereg$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.deleteTimereg),
        mergeMap(action => {
            const id = action.id;
            return this.timetrackService.deleteTimereg(id).pipe(
                map(() => TimetrackActions.deleteTimeregSuccess({ id })),
                catchError(() => EMPTY)
            );
        })
    ));

    updateTimeregBeskrivelse$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.updateTimeregBeskrivelse),
        delay(2000),
        concatMap(action => {
            return this.timetrackService.updateTimeregDescription(action.timetrackDescription).pipe(
                map(timeregs => TimetrackActions.updateTimeregBeskrivelseSuccess({ timeregs })),
                catchError(() => EMPTY)
            );
        })
    ));

    createKrKladde$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.createKrKladde),
        mergeMap(action => {
            let newKrKladde = new KrKladde();
            newKrKladde = {...newKrKladde, ...action.partialKrKladde};
            return this.timetrackService.createKrKladde(newKrKladde).pipe(
                map(krKladde => TimetrackActions.createKrKladdeSuccess({ krKladde })),
                catchError(() => EMPTY)
            );
        })
    ));

    updateKrKladdeBeskrivelse$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.updateKrKladdeBeskrivelse),
        delay(2000),
        mergeMap(action => {
            return this.timetrackService.updateKrKladdeDescription(action.timetrackDescription).pipe(
                map(krKladde => TimetrackActions.updateKrKladdeBeskrivelseSuccess({ krKladde })),
                catchError(() => EMPTY)
            );
        })
    ));

    searchActiveClient$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.searchActiveClient),
        withLatestFrom(this.clientSelector?.client$),
        map(([, client]) => {
            const searchFor = client.glkundenr !== -1 ? client.glkundenr.toString() : '';
            return TimetrackActions.search({ searchFor });
        })
    ));

    loadNormtider$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadNormtider),
        withLatestFrom(
            this.timetrackSelector.user$
        ),
        switchMap(([props, user]) => {
            if (!user) { return EMPTY; }
            return this.timetrackService.getNormtider(user.cardId, props.year).pipe(
                map(normtider => TimetrackActions.loadNormtiderSuccess({ normtider })),
                catchError(() => EMPTY)
            );
        })
    ));

    loadUgerepDone$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadUgerepDone),
        withLatestFrom(
            this.timetrackSelector.user$
        ),
        switchMap(([props, user]) => {
            if (!user) { return EMPTY; }
            return this.timetrackService.getUgerepDone(user.cardId, props.year).pipe(
                map(ugerepDone => TimetrackActions.loadUgerepDoneSuccess({ ugerepDone })),
                catchError(() => EMPTY)
            );
        })
    ));

    createUgerepDone$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.createUgerepDone),
        mergeMap(action => {
            return this.timetrackService.createUgerepDone(action.ugerepDone).pipe(
                map(ugerepDone => TimetrackActions.createUgerepDoneSuccess({ ugerepDone })),
                catchError(() => EMPTY)
            );
        })
    ));

    loadKroneKladde$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadKroneKladde),
        withLatestFrom(
            this.timetrackSelector.year$,
            this.timetrackSelector.user$
        ),
        switchMap(([_, year, user]) => {
            if (!user) { return EMPTY; }
            return this.timetrackService.getKroneKladde(user.cardId, year).pipe(
                map(sager => TimetrackActions.loadKroneKladdeSuccess({ sager })),
                catchError(() => EMPTY)
            );
        })
    ));

    loadKorebog$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadKorebog),
        withLatestFrom(
            this.timetrackSelector.year$,
            this.timetrackSelector.user$
        ),
        switchMap(([_, year, user]) => {
            if (!user) { return EMPTY; }
            return this.timetrackService.getKorebog(user.cardId, year).pipe(
                map(korebog => TimetrackActions.loadKorebogSuccess({ korebog })),
                catchError(() => EMPTY)
            );
        })
    ));

    createKorebog$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.createKorebog),
        mergeMap(action => {
            return this.timetrackService.createKorebog(action.korebog).pipe(
                map(korebog => TimetrackActions.createKorebogSuccess({ korebog })),
                catchError(() => EMPTY)
            );
        })
    ));

    updateKorebog$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.updateKorebog),
        mergeMap(action => {
            return this.timetrackService.updateKorebog(action.korebog).pipe(
                map(korebog => TimetrackActions.updateKorebogSuccess({ korebog })),
                catchError(() => EMPTY)
            );
        })
    ));

    deleteKorebog$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.deleteKorebog),
        mergeMap(action => {
            const id = action.id;
            return this.timetrackService.deleteKorebog(id).pipe(
                map(() => TimetrackActions.deleteKorebogSuccess({ id })),
                catchError(() => EMPTY)
            );
        })
    ));

    paidoutKorebog$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.paidoutKorebog),
        withLatestFrom(
            this.timetrackSelector.user$,
            this.timetrackSelector.year$
        ),
        mergeMap(([_, user, year]) => {
            if (!user) { return EMPTY; }
            return this.timetrackService.paidoutKorebog(user.cardId, year).pipe(
                map(korebog => TimetrackActions.paidoutKorebogSuccess({ korebog })),
                catchError(() => EMPTY)
            );
        })
    ));

    loadOfficeUgerepDone$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.loadOfficeUgerepDone),
        withLatestFrom(this.timetrackSelector.year$),
        switchMap(([_, year]) => {
            return this.timetrackService.getOfficeUgerepDone(year).pipe(
                map(officeUgerepDone => TimetrackActions.loadOfficeUgerepDoneSuccess({ officeUgerepDone })),
                catchError(() => EMPTY)
            );
        })
    ));

    deleteUgerepDone$ = createEffect(() => this.actions$.pipe(
        ofType(TimetrackActions.deleteUgerepDone),
        mergeMap(action => {
            const id = action.id;
            return this.timetrackService.deleteUgerepDone(id).pipe(
                map(() => TimetrackActions.deleteUgerepDoneSuccess({ id })),
                catchError(() => EMPTY)
            );
        })
    ));

    constructor(
        private actions$: Actions,
        private authSelector: AuthSelectors,
        private sessionSelector: SessionSelectors,
        private timetrackSelector: TimetrackSelectors,
        private timetrackService: TimetrackService,
        private clientSelector: ClientSelectors
    ) {}

}
